import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { isEqual } from "lodash";
import { useBaseTrackMap } from "../BaseTrackMap";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

const colourMap: Record<string, string> = {
  pushLowPower: "#bdffd4",
  pushHighPower: "#ffbdfc",
  lowPower: "#bdffd4",
  highPower: "#ffbdfc",
  out: "#ffc1c1",
  build: "#ffebb5",
  pit: "#badaff",
  unknown: "#919191",
};

// Get color based on lap type and power mode
const getLapColor = (
  type: string | null,
  highPower: boolean,
  isFullWetSession: boolean,
): string => {
  if (type === null) return colourMap.unknown;
  if (type === "Pit") return colourMap.pit;
  if (type === "Out" || type === "In" || type === "OutIn") return colourMap.out;

  // In wet session mode, show power mode colors for all full laps
  if (isFullWetSession) {
    return highPower ? colourMap.highPower : colourMap.lowPower;
  }

  if (type === "Slow") return colourMap.build;
  if (type === "LowPowerPush" || type === "HighPowerPush") {
    return highPower ? colourMap.pushHighPower : colourMap.pushLowPower;
  }
  return colourMap.pushLowPower; // default
};

function GPSTrackMap({
  raceMode = false,
  carColour,
}: {
  raceMode?: boolean;
  carColour?: string;
}) {
  const driverRunsData = useSelector(
    (state: RootState) => state.fpAnalysis.driverRuns,
    isEqual,
  );

  const isFullWetSession = useSelector(
    (state: RootState) => state.fpAnalysis.isFullWetSession,
  );

  const rgps = useSelector((state: RootState) => state.aks.v2.rgps, isEqual);

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  const participantDetails = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.participantDetails,
    isEqual,
  );

  const { containerRef, trackMap, rotatePoint, trackMapDatasets, bounds } =
    useBaseTrackMap({
      showFlagColors: true,
      showYellowSectors: true,
      showTimingLoops: true,
      showTurnNumbers: true,
    });

  // Build driver GPS datasets
  const driverDatasets = useMemo(() => {
    if (!rgps) return [];

    const excludedKeys = ["MED", "SC", "RES"];
    return Object.entries(rgps)
      .filter(([driverKey, data]) => {
        if (excludedKeys.includes(driverKey) || !data) return false;
        if (data.lat === undefined || data.lon === undefined) return false;
        // Only show if participant status is TRACK
        const participant =
          participantDetails?.[driverKey as keyof typeof participantDetails];
        return ["TRACK", "OUT_LAP", "STOPPED"].includes(
          participant?.status || "",
        );
      })
      .map(([driverKey, data]) => {
        const info = driverInfo?.[driverKey];
        const driverName = info?.driverName || `Driver ${driverKey}`;
        const isJagCar = Object.values(jagDrivers || {}).includes(driverKey);
        const isHighlightedDriver =
          !!carColour && jagDrivers?.[carColour] === driverKey;
        const markerColor = info?.driverTeamColour || "#333333";
        const labelBackgroundColor = isJagCar
          ? info?.driverHighlightColourAlt ||
            info?.driverTeamColour ||
            "#333333"
          : "#ffffff";
        const driverFontColour = isJagCar
          ? info?.driverHighlightColourFontColourAlt ||
            info?.driverFontColour ||
            "#000000"
          : info?.driverTeamColour || "#333333";

        // Get latest lap info for this driver
        const driverRuns = driverRunsData?.[driverKey];
        const latestLap = driverRuns?.[driverRuns.length - 1];
        // Only color based on lap type if it's an ongoing lap (duration is null)
        const lapColor =
          latestLap && latestLap.duration === null
            ? getLapColor(latestLap.type, latestLap.highPower, isFullWetSession)
            : colourMap.unknown;

        // Convert lat/lon from scaled integers to decimal degrees
        const lat = (data.lat || 0) / 1000000;
        const lon = (data.lon || 0) / 1000000;

        return {
          label: driverName,
          data: [rotatePoint(lon, lat)],
          backgroundColor: markerColor,
          pointBorderColor: "#ffffff",
          pointRadius: isHighlightedDriver ? 6 : 5,
          pointBorderWidth: 1,
          driverKey,
          lapColor,
          labelBackgroundColor,
          driverFontColour,
          isJagCar,
          isDriver: true,
        };
      })
      .sort((a, b) => Number(b.isJagCar) - Number(a.isJagCar));
  }, [
    rgps,
    driverInfo,
    jagDrivers,
    carColour,
    driverRunsData,
    participantDetails,
    isFullWetSession,
    rotatePoint,
  ]);

  const chartData = useMemo(() => {
    return { datasets: [...driverDatasets, ...trackMapDatasets] };
  }, [trackMapDatasets, driverDatasets]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      events: [],
      plugins: {
        datalabels: {
          display: (context: Context) => {
            // Only show labels for single point datasets
            return context.dataset.data.length === 1;
          },
          color: (context: Context) => {
            const dataset = context.dataset as {
              isDriver?: boolean;
              driverFontColour?: string;
            };
            if (!dataset.isDriver) return "#ffffff";
            return raceMode ? dataset.driverFontColour || "#000000" : "#000000";
          },
          backgroundColor: (context: Context) => {
            const dataset = context.dataset as {
              isDriver?: boolean;
              lapColor?: string;
              labelBackgroundColor?: string;
            };
            if (!dataset.isDriver) return "transparent";
            return raceMode
              ? dataset.labelBackgroundColor || colourMap.unknown
              : dataset.lapColor || colourMap.unknown;
          },
          textStrokeColor: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver ? "transparent" : "#2c2c2c";
          },
          textStrokeWidth: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver ? 0 : 4;
          },
          borderRadius: 0,
          padding: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver
              ? { top: 1, bottom: 1, left: 2, right: 2 }
              : { top: 0, bottom: 0, left: 0, right: 0 };
          },
          font: { size: 10, weight: "bold" as const },
          formatter: (_value: unknown, context: Context) => {
            return context.dataset.label || "";
          },
          anchor: "center" as const,
          align: (context: Context) => {
            const dataset = context.dataset as { isDriver?: boolean };
            return dataset.isDriver ? ("right" as const) : ("center" as const);
          },
          offset: 8,
        },
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          type: "linear" as const,
          min: bounds.minLon,
          max: bounds.maxLon,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          type: "linear" as const,
          min: bounds.minLat,
          max: bounds.maxLat,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
      },
    }),
    [bounds, raceMode],
  );

  const hasTrackData = trackMap?.trackEdges?.inner?.lat?.length > 0;

  return (
    <div className={styles.container} ref={containerRef}>
      {!hasTrackData && (
        <div className={styles.noDataOverlay}>Track map not configured</div>
      )}
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
}

export default GPSTrackMap;
